<script setup>
import { ref, computed, watch, watchEffect, onMounted, onUnmounted } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { Link } from '@inertiajs/vue3';
// import IconChat from '@/Icons/Chat.vue';
// import IconCountries from '@/Icons/Countries.vue';
import IconRegions from '@/Icons/OptimizedIcons.vue';
import IconGroup from '@/Icons/OptimizedIcons.vue';
// import IconAgenda from '@/Icons/agenda.vue';
// import IconCandidates from '@/Icons/candidates.vue';
import Dropdown from '@/Components/Dropdown.vue';
// import LiveChat from '@/Layouts/Partials/LiveChat.vue';
import axios from 'axios';

const emit = defineEmits(['dropdown-open']);

const page = usePage();
const activeUrl = computed(() => page.url);
const showJobs = ref(false);
const mobileJobsMenu = ref(null);
const wrapper = ref(null);

const authuser = computed(() => page.props.auth?.user);

const css = {
  bull_active: 'w-2 h-2 bg-main rounded-full',
  bull: 'w-1 h-1 bg-grey-500 dark:bg-grey-400 rounded-full whitespace-nowrap',
  label_active: 'text-theme-primary font-semibold',
  label_active_sub: 'text-black dark:text-theme-primary font-semibold',
  label: 'text-theme-secondary',
  bg_active: 'lg:bg-grey-8 lg:dark:bg-opacity-10 active',
  bg_inactive:
    'bg-theme-paper bg-opacity-0 dark:bg-transparent transition-all lg:hover:bg-grey-8 lg:dark:hover:bg-dark-neutral lg:focus:bg-grey-8 lg:dark:focus:bg-dark-neutral',
};

const isJobListUrl = (url) => {
  return url.startsWith('/jobs');
};

const toggleJobs = () => {
  showJobs.value = !showJobs.value;
};

const isActive = (url) => {
  return activeUrl.value === url;
};

watch(activeUrl, (newUrl, oldUrl) => {
  if (newUrl.startsWith('/jobs') && !oldUrl?.startsWith('/jobs')) {
    showJobs.value = true;
  } else if (!newUrl.startsWith('/jobs')) {
    showJobs.value = false;
  }
});

watch(showJobs, (newValue) => {
  emit('dropdown-open', newValue);
});

const handleJobsDropdownChange = (isOpen) => {
  showJobs.value = isOpen;
};

onMounted(() => {
  const handleClickOutside = (event) => {
    if (window.innerWidth < 1024) {
      if (mobileJobsMenu.value && !mobileJobsMenu.value.contains(event.target)) {
        showJobs.value = false;
      }
    }
  };

  document.addEventListener('click', handleClickOutside);

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
  });
});
</script>

<template>
  <div ref="wrapper" class="flex flex-col w-full transition-all lg:items-center group/wrapper">
    <div class="flex flex-col gap-px lg:gap-px lg:items-center lg:mx-auto lg:px-4 lg:flex-row">
      <div class="lg:hidden shrink-0" ref="mobileJobsMenu">
        <Link
          :href="route('jobs')"
          class="inline-flex items-center justify-start flex-shrink-0 w-full h-[51px] gap-1 px-6 transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group overflow-hidden lg:overflow-visible"
          :class="[isJobListUrl(activeUrl) ? css.bg_active : css.bg_inactive]"
          @click.prevent.stop="toggleJobs"
        >
          <div class="inline-flex justify-center truncate transition-all grow shrink basis-0">
            <div
              class="text-xs font-semibold truncate whitespace-nowrap"
              :class="isJobListUrl(activeUrl) ? css.label_active : css.label"
            >
              {{ $t('Jobs') }}
            </div>
          </div>
          <div
            class="flex items-center justify-center ml-auto transition-transform"
            :class="showJobs ? 'rotate-0' : '-rotate-90'"
          >
            <div class="relative w-4 h-4">
              <IconGroup  type="arrow-down" />
            </div>
          </div>
        </Link>

        <Link
          v-if="showJobs"
          :href="route('jobs')"
          @click="showJobs = false"
          class="flex items-center pl-4 overflow-hidden text-left transition-all bg-opacity-0 h-9 lg:hover:bg-grey-8 lg:dark:hover:bg-dark-neutral lg:overflow-visible"
        >
          <div class="flex items-center justify-center">
            <div class="flex items-center justify-center w-6 h-6">
              <div :class="isActive('/jobs') ? css.bull_active : css.bull"></div>
            </div>
          </div>
          <div class="inline-flex flex-col truncate transition-all grow">
            <div
              class="text-xs font-semibold truncate whitespace-nowrap"
              :class="isActive('/jobs') ? css.label_active_sub : css.label"
            >
              {{ $t('All jobs') }}
            </div>
          </div>
        </Link>

        <Link
          v-if="showJobs"
          href="/jobs/applied"
          @click="showJobs = false"
          class="flex items-center pl-4 overflow-hidden text-left transition-all bg-opacity-0 h-9 lg:hover:bg-grey-8 lg:dark:hover:bg-dark-neutral lg:overflow-visible"
        >
          <div class="flex items-center justify-center">
            <div class="flex items-center justify-center w-6 h-6">
              <div :class="isActive('/jobs/applied') ? css.bull_active : css.bull"></div>
            </div>
          </div>
          <div class="inline-flex flex-col truncate transition-all grow">
            <div
              class="text-xs font-semibold truncate whitespace-nowrap"
              :class="isActive('/jobs/applied') ? css.label_active_sub : css.label"
            >
              {{ $t('Applied') }}
            </div>
          </div>
        </Link>

        <Link
          v-if="showJobs"
          href="/jobs/saved"
          @click="showJobs = false"
          class="flex items-center pl-4 overflow-hidden text-left transition-all bg-opacity-0 h-9 lg:hover:bg-grey-8 lg:dark:hover:bg-dark-neutral lg:overflow-visible"
        >
          <div class="flex items-center justify-center">
            <div class="flex items-center justify-center w-6 h-6">
              <div :class="isActive('/jobs/saved') ? css.bull_active : css.bull"></div>
            </div>
          </div>
          <div class="inline-flex flex-col truncate transition-all grow">
            <div
              class="text-xs font-semibold truncate whitespace-nowrap"
              :class="isActive('/jobs/saved') ? css.label_active_sub : css.label"
            >
              {{ $t('Saved') }}
            </div>
          </div>
        </Link>
      </div>

      <div class="hidden lg:block shrink-0">
        <Dropdown
          contentClasses="py-1 bg-grey-8 dark:bg-primary !ring-0 !rounded-t-none"
          overlayClasses="!mt-0"
          width="full"
          align="left"
          @menuStateChange="handleJobsDropdownChange"
        >
          <template #trigger>
            <Link
              :href="route('jobs')"
              class="inline-flex items-center justify-start flex-shrink-0 w-full h-[51px] gap-1 px-6 transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group overflow-hidden lg:overflow-visible"
              :class="[
                isJobListUrl(activeUrl) ? css.bg_active : css.bg_inactive,
                showJobs ? 'lg:rounded-b-none' : '',
              ]"
            >
              <div class="inline-flex justify-center truncate transition-all grow shrink basis-0">
                <div
                  class="text-xs font-semibold truncate whitespace-nowrap"
                  :class="isJobListUrl(activeUrl) ? css.label_active : css.label"
                >
                  {{ $t('Jobs') }}
                </div>
              </div>
              <div
                class="flex items-center justify-center transition-transform"
                :class="showJobs ? 'rotate-0' : '-rotate-90'"
              >
                <div class="relative w-4 h-4">
                  <IconGroup  type="arrow-down" />
                </div>
              </div>
            </Link>
          </template>

          <template #content>
            <div>
              <Link
                :href="route('jobs')"
                class="flex items-center self-stretch justify-start w-full overflow-hidden transition-all bg-opacity-0 h-9 hover:bg-grey-16 dark:hover:bg-dark-neutral lg:overflow-visible"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/jobs') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div
                  class="inline-flex flex-col truncate transition-all grow lg:text-left lg:items-start lg:justify-start"
                >
                  <div
                    class="text-xs font-semibold truncate whitespace-nowrap"
                    :class="isActive('/jobs') ? css.label_active_sub : css.label"
                  >
                    {{ $t('All jobs') }}
                  </div>
                </div>
              </Link>

              <Link
                href="/jobs/applied"
                class="flex items-center self-stretch justify-start w-full overflow-hidden transition-all bg-opacity-0 h-9 hover:bg-grey-16 dark:hover:bg-dark-neutral lg:overflow-visible"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/jobs/applied') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div
                  class="inline-flex flex-col truncate transition-all grow lg:text-left lg:items-start lg:justify-start"
                >
                  <div
                    class="text-xs font-semibold truncate whitespace-nowrap"
                    :class="isActive('/jobs/applied') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Applied') }}
                  </div>
                </div>
              </Link>

              <Link
                href="/jobs/saved"
                class="flex items-center self-stretch justify-start w-full overflow-hidden transition-all bg-opacity-0 h-9 hover:bg-grey-16 dark:hover:bg-dark-neutral lg:overflow-visible"
              >
                <div class="flex items-center justify-center">
                  <div class="flex items-center justify-center w-6 h-6">
                    <div :class="isActive('/jobs/saved') ? css.bull_active : css.bull"></div>
                  </div>
                </div>
                <div
                  class="inline-flex flex-col truncate transition-all grow lg:text-left lg:items-start lg:justify-start"
                >
                  <div
                    class="text-xs font-semibold truncate whitespace-nowrap"
                    :class="isActive('/jobs/saved') ? css.label_active_sub : css.label"
                  >
                    {{ $t('Saved') }}
                  </div>
                </div>
              </Link>
            </div>
          </template>
        </Dropdown>
      </div>

      <Link
        :href="route('job', { name: 'companies' })"
        :class="isActive('/companies') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-[51px] gap-1 px-6 overflow-hidden transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group lg:overflow-visible"
      >
        <div class="inline-flex justify-center transition-all grow shrink basis-0">
          <div
            class="text-xs font-semibold whitespace-nowrap"
            :class="isActive('/companies') ? css.label_active : css.label"
          >
            {{ $t('Companies') }}
          </div>
        </div>
      </Link>

      <!-- <Link
                :href="route('job', { name: 'regions' })"
                :class="isActive('/regions') ? css.bg_active : css.bg_inactive"
                class="inline-flex items-center justify-start flex-shrink-0 w-full h-10 gap-1 px-4 transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group"
            >
                <div class="flex items-center justify-center">
                    <div class="relative flex items-center justify-center w-6 h-6">
                        <IconRegions  type="regions" />
                    </div>
                </div>
                <div class="inline-flex justify-center transition-all grow shrink basis-0">
                    <div
                        class="text-xs font-semibold lg:text-sm whitespace-nowrap"
                        :class="isActive('/regions') ? css.label_active : css.label"
                    >
                        {{ $t("Regions") }}
                    </div>
                </div>
            </Link> -->
      <!-- <Link
                :href="route('chat.index')"
                @click.prevent="updateActiveUrl('/chat')"
                :class="isActive('/chat') ? css.bg_active : css.bg_inactive"
                class="flex-col items-center justify-start flex-shrink-0 hidden transition-all bg-theme-paper lg:inline-flex lg:w-full lg:rounded-lg lg:bg-theme-paper lg:hover:bg-grey-8 lg:max-w-none lg:p-2 lg:pl-3 lg:pr-2 lg:flex-row min-h-11 group"
            >
                <div class="flex items-center justify-center lg:pr-4">
                    <div class="relative w-6 h-6">
                        <IconChat />
                    </div>
                </div>
                <div
                    class="flex-col items-center justify-center text-center lg:pr-4 grow shrink basis-0 lg:text-left lg:items-start lg:justify-start"
                >
                    <div
                        class="text-xs lg:text-sm"
                        :class="
                            isActive('/chat') ? css.label_active : css.label
                        "
                    >
                        {{ $t("Chat") }}
                    </div>
                </div>
                <div
                    v-if="unreadMessagesCount"
                    class="w-fit px-1.5 py-1 bg-main bg-opacity-20 rounded-md justify-start items-center gap-1.5 flex"
                >
                    <div
                        class="text-xs font-bold leading-tight text-center text-main"
                    >
                        {{ unreadMessagesCount }}
                    </div>
                </div>
            </Link> -->

      <!-- <Link
                :href="route('agendas.index', 'candidate')"
                @click.prevent="updateActiveUrl('/agenda/candidate')"
                :class="isActive('/agenda/candidate') ? css.bg_active : css.bg_inactive"
                class="inline-flex items-center self-stretch justify-start p-2 pl-3 pr-2 transition-all rounded-lg min-h-11 hover:bg-grey-8 group"
            >
                <div class="flex items-center justify-center lg:pr-4">
                    <div class="relative w-6 h-6">
                        <IconAgenda />
                    </div>
                </div>
                <div class="inline-flex flex-col items-center justify-center text-center lg:pr-4 grow shrink basis-0 lg:text-left lg:items-start lg:justify-start">
                    <div :class="isActive('/agenda/candidate') ? css.label_active : css.label">
                        {{ $t("Agenda") }}
                    </div>
                </div>
            </Link> -->

      <!-- <Link
                :href="route('group.index')"
                @click.prevent="updateActiveUrl('/group')"
                :class="isActive('/group') ? css.bg_active : css.bg_inactive"
                class="inline-flex items-center self-stretch justify-start p-2 pl-3 pr-2 transition-all rounded-lg min-h-11 hover:bg-grey-8 group"
            >
                <div class="flex items-center justify-center lg:pr-4">
                    <div class="relative w-6 h-6">

							<IconGroup  type="nav-group" />
                    </div>
                </div>
                <div class="inline-flex flex-col items-center justify-center text-center lg:pr-4 grow shrink basis-0 lg:text-left lg:items-start lg:justify-start">
                    <div :class="isActive('/groups') ? css.label_active : css.label">
                       <span class=" group-[&.active]:!text-main group-[&.active]:font-semibold"> {{ $t("Groups") }}</span>
                    </div>
                </div>
            </Link> -->

      <!-- </div>
		  <div class="flex flex-col items-start self-stretch justify-start gap-1 p-2 grow shrink basis-0"> -->

      <Link
        href="/journey"
        :class="isActive('/journey') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-[51px] gap-1 px-6 overflow-hidden transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group lg:overflow-visible"
      >
        <div class="inline-flex justify-center transition-all grow shrink basis-0">
          <div
            class="text-xs font-semibold whitespace-nowrap"
            :class="isActive('/journey') ? css.label_active : css.label"
          >
            {{ $t('Career AI') }}
          </div>
        </div>
      </Link>

      <Link
        :href="route('pay-peek.preview')"
        :class="isActive('/pay-check') ? css.bg_active : css.bg_inactive"
        class="inline-flex items-center justify-start flex-shrink-0 w-full h-[51px] gap-1 px-6 overflow-hidden transition-all lg:rounded-lg lg:w-fit lg:h-12 min-w-28 group lg:overflow-visible"
      >
        <div class="inline-flex justify-center transition-all grow shrink basis-0">
          <div
            class="text-xs font-semibold whitespace-nowrap"
            :class="isActive('/pay-check') ? css.label_active : css.label"
          >
            {{ $t('PayCheck') }}
          </div>
        </div>
      </Link>
    </div>
  </div>
  <!-- <LiveChat></LiveChat> -->
</template>
<style>
.BeaconFabButtonFrame {
  bottom: 3.75rem !important;
}
@media (min-width: 740px) {
  .BeaconFabButtonFrame {
    bottom: 0.625rem !important;
  }
}
</style>
